import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { listAllClientTags } from '@brands/services/identity/listAllClientTags';
import { ClientTag } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { ColumnVisibilityKey } from '@brands/store/slices/columnVisibilitySlice';
import { TablePaginationKey } from '@brands/store/slices/tablePaginationSlice';
import { getFriendlyName } from '@brands/Utils/getFriendlyName';
import TextField from '@mui/material/TextField';
import { isAxiosError } from 'axios';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { usePersistedRowsPerPage } from '../../../../hooks/usePersistedRowsPerPage';
import CADataGrid, { caGridStyles, ColumnDef } from '../../CareAdvisor/Components/CADataGrid';
import parentStyles from '../styles.module.scss';

const filterInputSx = { '& .MuiInputBase-input': { fontSize: 13, padding: '4px 2px' } };

// The endpoint returns every client the caller may see in one response and has
// no limit/offset, so sorting, filtering and paging all happen here. That is
// fine at the size the design draws (13 rows) but is the reason this page does
// not ask the server for a page — there is no page to ask for.
const ClientsList = (): JSX.Element => {
  const { userInfo } = useAppSelector(selectAuth);
  const [clients, setClients] = useState<ClientTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'forbidden' | 'generic' | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = usePersistedRowsPerPage(TablePaginationKey.SuperAdminClients, 10);
  const [filters, setFilters] = useState<{ name?: string; code?: string }>({});
  const [sortColKey, setSortColKey] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAllClientTags(userInfo?.role?.name);
      setClients(data?.nodes ?? []);
    } catch (err) {
      // The grid renders its own message for these two, so a toast on top of it
      // would say the same thing twice.
      setError(isAxiosError(err) && err.response?.status === 403 ? 'forbidden' : 'generic');
    } finally {
      setLoading(false);
    }
  }, [userInfo?.role?.name]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleFilterChange = useCallback((key: 'name' | 'code', value: string) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value === '') delete next[key];
      else next[key] = value;
      return next;
    });
    setPage(0);
  }, []);

  const handleSort = useCallback(
    (key: string) => {
      setPage(0);
      if (sortColKey === key) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortColKey(key);
        setSortDirection('asc');
      }
    },
    [sortColKey],
  );

  const visibleRows = useMemo(() => {
    const needleName = (filters.name ?? '').toLowerCase();
    const needleCode = (filters.code ?? '').toLowerCase();
    const filtered = clients.filter(
      (c) =>
        (c.name ?? '').toLowerCase().includes(needleName) && (c.code ?? '').toLowerCase().includes(needleCode),
    );
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const left = String((sortColKey === 'code' ? a.code : a.name) ?? '');
      const right = String((sortColKey === 'code' ? b.code : b.name) ?? '');
      return left.localeCompare(right) * dir;
    });
  }, [clients, filters.name, filters.code, sortColKey, sortDirection]);

  const pagedRows = useMemo(
    () => visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [visibleRows, page, rowsPerPage],
  );

  const columns: ColumnDef<ClientTag>[] = useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        widthPx: 280,
        sortable: true,
        renderCell: (row) => <span className={caGridStyles.cellText}>{row.name}</span>,
        filterContent: (
          <TextField
            variant="standard"
            placeholder="Name"
            value={filters.name ?? ''}
            onChange={(e) => handleFilterChange('name', e.target.value)}
            InputProps={{ disableUnderline: true }}
            sx={filterInputSx}
            fullWidth
          />
        ),
      },
      {
        key: 'code',
        label: 'Tag',
        widthPx: 200,
        sortable: true,
        renderCell: (row) => <span className={caGridStyles.cellText}>{row.code ?? '—'}</span>,
        filterContent: (
          <TextField
            variant="standard"
            placeholder="Tag"
            value={filters.code ?? ''}
            onChange={(e) => handleFilterChange('code', e.target.value)}
            InputProps={{ disableUnderline: true }}
            sx={filterInputSx}
            fullWidth
          />
        ),
      },
      {
        key: 'case_types',
        label: 'Services',
        widthPx: 360,
        renderCell: (row) => (
          <span className={caGridStyles.cellText}>
            {row.case_types?.length ? row.case_types.map((t) => getFriendlyName(t) || t).join(', ') : '—'}
          </span>
        ),
      },
    ],
    [filters.name, filters.code, handleFilterChange],
  );

  return (
    <div className={parentStyles.casesPageContainer}>
      <div className={parentStyles.casesPageHeader}>
        {/* Page heading — h1, matching the admin Cases and Audit trail headings. */}
        <h1 className={parentStyles.sectionTitle}>Clients</h1>
      </div>

      <div className={parentStyles.casesGridWrapper}>
        {loading && clients.length === 0 ? (
          <Loading dotted />
        ) : (
          <CADataGrid
            columns={columns}
            rows={pagedRows}
            getRowId={(row) => row.id}
            count={visibleRows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 100]}
            onPageChange={setPage}
            onRowsPerPageChange={(rpp) => {
              setRowsPerPage(rpp);
              setPage(0);
            }}
            loading={loading}
            error={error}
            onRetry={() => {
              void load();
            }}
            emptyMessage="No clients found"
            sortBy={sortColKey}
            sortDirection={sortDirection}
            onSort={handleSort}
            columnVisibilityKey={ColumnVisibilityKey.SuperAdminClients}
          />
        )}
      </div>
    </div>
  );
};

export default ClientsList;
